import { useEffect, useState, ReactNode, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import fetchAgencies, { Agency } from '../../api/agency/settings/fetchAcencies';
import { useTranslation } from 'react-i18next';
import { PageWithSidePanel } from '../../componentsNew/atoms/page-with-side-panel';
import { PageContainer } from '../../componentsNew/atoms/page-container';
import TopNavigation from '../../components/navigation/TopNavigation';
import { Main } from '../../componentsNew/organisms/main';
import { SidePanel } from '../../componentsNew/organisms/side-panel';
import { TemplateVariableExplanation } from '../../componentsNew/templates/variable-explanation';
import PageHeader from '../../componentsNew/molecules/pageHeader/PageHeader';
import Text from '../../componentsNew/atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../componentsNew/enums';
import styles from './SettingsPage.module.css';
import { routeByPathname } from '../../routes';
import { useRoutePath } from '../../componentsNew/utils/useRoutePath';
import SettingsFooter from './SettingsFooter';
import Form, { Data, FormData, FormErrors } from '../../componentsNew/molecules/form/Form';
import SkeletonComponent from '../../components/SkeletonComponent';
import HorizontalTabs from '../../componentsNew/molecules/horizontal-tabs/HorizontalTabs';
import { TabData } from '../../componentsNew/atoms/tab';
import saveSettings, { SaveSettingsPayload } from '../../api/agency/settings/saveSettings';
import fetchSettings, { FetchSettingsResponse } from '../../api/agency/settings/fetchSettings';
import Settings4LineIcon from '../../componentsNew/atoms/icons/Settings4LineIcon';
import MailStarLineIcon from '../../componentsNew/atoms/icons/MailStarLineIcon';
import Notifications from '../../componentsNew/organisms/notifications/Notifications';
import HighlightBlock from '../../componentsNew/molecules/highlightBlock/HighlightBlock';
import SelectNew, { Option } from '../../componentsNew/organisms/select-new/SelectNew';
import { useFeature } from '@growthbook/growthbook-react';
import MailUnreadLineIcon from '../../componentsNew/atoms/icons/MailUnreadLineIcon';

type AgencyEmail = {
    id: string;
    email: string;
}
const SettingsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const routePath = useRoutePath();
    const location = useLocation();
    const currentRoute = routeByPathname(location.pathname);
    const [ queryParams, setQueryParams ] = useSearchParams();

    const [ isFetchingAgencies, setIsFetchingAgencies ] = useState(true);
    const [ agencyOptions, setAgencyOptions ] = useState<Option[]>([]);
    const [ selectedAgencyId, setSelectedAgencyId ] = useState<string | null>('null');
    const [ previousSelectedAgencyId, setPreviousSelectedAgencyId ] = useState<string | null>('null');
    const [ agencyEmails, setAgencyEmails ] = useState<AgencyEmail[]>([]);
    const [ selectedAgencyEmail, setSelectedAgencyEmail ] = useState<string>('');

    const abortControllerRef = useRef<AbortController | null>(null);
    const fetchingStoredDataRef = useRef<boolean>(false);

    const [ storedData, setStoredData ] = useState<FetchSettingsResponse | null>(null);

    const [ isSubmittingSettings, setIsSubmittingSettings ] = useState<boolean>(false);
    const [ isSubmittingSettingsSuccess, setIsSubmittingSettingsSuccess ] = useState<boolean>(false);

    const [formData, setFormData] = useState<Data>({});
    const [, setIsFormValid] = useState<boolean>(false);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [hasFormBeenChanged, setHasFormBeenChanged] = useState<boolean>(false);

    const EMAIL_NOTIFICATION_SETTINGS_TAB_FEATURE = useFeature('email-notification-settings-tab').value;

    useEffect(() => {
        tryFetchingAgencies();
    }, []);

    const tryFetchingAgencies = async () => {
        setIsFetchingAgencies(true);

        try {
            const response = await fetchAgencies();
            createAgencyOptionsAndEmails(response.result);

        } catch {
            Notifications.add({
                type: 'failed',
                message: t('settingsPage.fetching.agencies.failed')
            });
        } finally {
            setIsFetchingAgencies(false);
        }
    }

    const handleSetSelectedAgencyEmail = (emailArray: AgencyEmail[], agencyId: string) => {
        const agencyEmail: AgencyEmail = emailArray.find((agency) => agency.id === agencyId);
        setSelectedAgencyEmail(agencyEmail?.email || '');
    }

    const createAgencyOptionsAndEmails = (agencies: Agency[]) => {
        const agencyOptions: Option[] = [];
        const agencyEmailsArray: AgencyEmail[] = [];

        agencies.forEach((agency) => {
            agencyOptions.push(
                {
                    label: agency.name,
                    value: agency.id
                }
            );

            agencyEmailsArray.push(
                {
                    id: agency.id,
                    email: agency.defaultEmail
                }
            );
        });

        if (agencyOptions && agencyOptions[0]) {
            setAgencyOptions(agencyOptions);

            let foundAgency = null;


            if (selectedAgencyId && selectedAgencyId !== 'null') {
                foundAgency = !agencyOptions.find((agency) => agency.value === selectedAgencyId);
            }

            if (foundAgency === null) {
                const agencyId = agencyOptions[0].value;
                setSelectedAgencyId(agencyId);
                handleSetSelectedAgencyEmail(agencyEmailsArray, agencyId);
            }
        }

        if (agencyEmailsArray && agencyEmailsArray.length > 0) {
            setAgencyEmails(agencyEmailsArray);
        }
    }

    useEffect(() => {
        if (
            !selectedAgencyId
            || selectedAgencyId === 'null'
            || selectedAgencyId === previousSelectedAgencyId
        ) {
            return;
        }

        if (!previousSelectedAgencyId || previousSelectedAgencyId === 'null') {
            window.history.replaceState({}, '', `${window.location.pathname}?selectedAgencyId=${selectedAgencyId}`);
        } else {
            queryParams.set('selectedAgencyId', selectedAgencyId);
            setQueryParams(queryParams);
        }


        tryFetchingStoredData(selectedAgencyId);

        setPreviousSelectedAgencyId(selectedAgencyId);

    }, [selectedAgencyId]);

    useEffect(() => {
        const agencyId = queryParams.get('selectedAgencyId');
        setSelectedAgencyId(agencyId);
        handleSetSelectedAgencyEmail(agencyEmails, agencyId);
    }, [queryParams]);

    useEffect(() => {
        if (abortControllerRef.current !== null) {
            abortControllerRef.current.abort();
            fetchingStoredDataRef.current = false;
        }

        if (!selectedAgencyId || selectedAgencyId === 'null') {
            return;
        }

        tryFetchingStoredData(selectedAgencyId);
    }, [currentRoute.name]);

    const tryFetchingStoredData = async (selectedAgencyId: string) => {
        if (fetchingStoredDataRef.current) {
            return;
        }

        fetchingStoredDataRef.current = true;

        try {
            abortControllerRef.current = new AbortController();
            const fetchingStoredDataSignal: AbortSignal = abortControllerRef.current.signal;
            const response = await fetchSettings(currentRoute.name, selectedAgencyId, fetchingStoredDataSignal);
            const responseClone = { ...response };
            delete responseClone['agencyId'];

            setStoredData(responseClone);

            fetchingStoredDataRef.current = false;
        } catch (error) {
            if (error.name === 'AbortError') {
                return;
            }

            fetchingStoredDataRef.current = false;

            Notifications.add({
                type: 'failed',
                message: t('settingsPage.general.failed.fetching')
            });
        }
    }

    const navigateToTab = (tab: TabData) => {
        navigate({
            pathname: routePath(tab.value),
            search: queryParams.toString()
        });
    }

    const handleChangeAgency = (selectedOptions: Option[]) => {
        if (selectedOptions.length > 0) {
            const agencyId = selectedOptions[0].value;
            setSelectedAgencyId(agencyId);
            handleSetSelectedAgencyEmail(agencyEmails, agencyId);
        }
    }

    const handleFormDataChange = (data: Data, isValid: boolean, errors: FormErrors, hasFormBeenChanged: boolean) => {
        setFormData(data);
        setIsFormValid(isValid);
        setFormErrors(errors);
        setHasFormBeenChanged(hasFormBeenChanged);
    }

    const handleSubmit = async () => {
        setIsSubmittingSettingsSuccess(false);
        setIsSubmittingSettings(true);

        const startTime = Date.now();

        const payload = Object.fromEntries(
            Object.entries(formData).map(([key, val]) => [key, val.value])
        ) as unknown as SaveSettingsPayload;

        const finalPayload = {
            agencyId: selectedAgencyId,
            ...payload
        };

        try {
            await saveSettings(currentRoute.name, finalPayload);

            Notifications.add({
                type: 'success',
                message: t('settingsPage.signature.saved'),
            });

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(300 - elapsedTime, 0);

            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setIsSubmittingSettings(false);
            setIsSubmittingSettingsSuccess(true);

            await new Promise(resolve => setTimeout(resolve, 300 + 150));
        } catch {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        } finally {
            setIsSubmittingSettings(false);
            setIsSubmittingSettingsSuccess(false);
        }
    }

    const renderChildPage = (): ReactNode => {
        if (storedData === null) {
            return;
        }

        return (
            <currentRoute.component
                storedData={storedData}
                formData={formData}
                formErrors={formErrors}
                isLoading={isFetchingAgencies || fetchingStoredDataRef.current}
                agencyEmail={selectedAgencyEmail}
            />
        );
    }

    const constructTabArray = (): TabData[] => {
        const tabs: TabData[] = [
            {
                label: 'Algemeen',
                value: 'SettingsGeneral',
                icon: <Settings4LineIcon/>
            },
            {
                label: 'E-mail templates',
                value: 'SettingsEmailTemplates',
                icon: <MailStarLineIcon/>
            }
        ];

        if (EMAIL_NOTIFICATION_SETTINGS_TAB_FEATURE) {
            tabs.push({
                label: 'E-mail notificaties',
                value: 'SettingsEmailNotifications',
                icon: <MailUnreadLineIcon/>
            });
        }

        return tabs;
    }

    return (
        <>
            <PageWithSidePanel>
                <PageContainer>
                    <TopNavigation showBackButton/>
                    <Form
                        initialFormData={storedData as unknown as FormData}
                        onChange={handleFormDataChange}
                        onSubmit={handleSubmit}
                    >
                        <Main>
                            <PageHeader title={'settingsPage.title'}/>
                            <HorizontalTabs
                                active={currentRoute.name}
                                onChange={navigateToTab}
                                tabs={constructTabArray()}
                            />
                            {currentRoute.name === 'SettingsEmailTemplates' &&
                                <div className={styles.contentSection}>
                                    <div className={styles.settingsPanel}>
                                        <div className={styles.formRow}>
                                            <HighlightBlock>
                                                <Text
                                                    size={FontSizeEnum.Sm}
                                                    color={ColorEnum.Gray800}
                                                    tag='p'
                                                >
                                                    {t('settingsPage.signature.info')}
                                                </Text>
                                            </HighlightBlock>
                                        </div>
                                    </div>
                                </div>
                            }
                            {((isFetchingAgencies || fetchingStoredDataRef.current) && agencyOptions.length === 0) ?
                                <div className={styles.contentSection}>
                                    <div className={styles.descriptionPanel}>
                                        <SkeletonComponent height={24} width={'100%'}/>
                                        <SkeletonComponent height={50} width={'100%'}/>
                                    </div>
                                    <div className={styles.settingsPanel}>
                                        <div className={styles.formRow}>
                                            <SkeletonComponent height={40} width={200}/>
                                        </div>
                                    </div>
                                </div>
                                :
                                <>
                                    {agencyOptions.length > 1 &&
                                        <>
                                            <div className={styles.contentSection}>
                                                <div className={styles.descriptionPanel}>
                                                    <Text
                                                        color={ColorEnum.Gray800}
                                                        weight={FontWeightEnum.Bold}
                                                        tag='h6'
                                                    >
                                                        {t('settingsPage.agencySelect.title')}
                                                    </Text>
                                                    <Text
                                                        size={FontSizeEnum.Sm}
                                                        color={ColorEnum.Gray800}
                                                        tag='p'
                                                    >
                                                        {t('settingsPage.agencySelect.description')}
                                                    </Text>
                                                </div>
                                                <div className={styles.settingsPanel}>
                                                    <div className={styles.formRow}>
                                                        <div className={styles.selectContainer}>
                                                            <SelectNew
                                                                selected={selectedAgencyId}
                                                                options={agencyOptions}
                                                                onChange={handleChangeAgency}
                                                                hasInputField={agencyOptions.length > 10}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <hr/>
                                        </>
                                    }
                                    {renderChildPage()}
                                </>
                            }
                        </Main>
                        <SettingsFooter
                            isLoading={isFetchingAgencies || fetchingStoredDataRef.current}
                            hasFormBeenChanged={hasFormBeenChanged}
                            isSubmitting={isSubmittingSettings}
                            isSuccessful={isSubmittingSettingsSuccess}
                        />
                    </Form>
                </PageContainer>
                <SidePanel title={t('sidepanel.variable.title')}>
                    <TemplateVariableExplanation/>
                </SidePanel>
            </PageWithSidePanel>
        </>
    );
}

export default SettingsPage;
